import { Injectable } from '@angular/core';
import * as Auth from 'aws-amplify/auth';
import * as Storage from 'aws-amplify/storage';
import * as AWS from 'aws-sdk';
import { BehaviorSubject, Observable } from 'rxjs';
import { HttpService } from './http.service';

import { environment } from '__env';
import { ProfileService } from '__services';
import { LodashUtil } from '__utils';

@Injectable({
  providedIn: 'root',
})
export class AwsService {
  private _cached: any;

  setNewPassword: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  confirmSMS: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  loginValue: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  preventLogout: boolean = false;

  constructor(
    private profileService: ProfileService,
    private http: HttpService,
  ) {}

  setCached(info: any) {
    this._cached = info;
  }

  getCached() {
    return this._cached;
  }

  clearCached() {
    this._cached = null;
    Auth.getCurrentUser;
  }

  login(payload: { username: string; password: string }): Promise<any> {
    return Auth.signIn(payload);
  }

  logout(): Promise<any> {
    return Auth.signOut();
  }

  currentUser(): Promise<any> {
    return Auth.getCurrentUser();
  }

  fetchAuthSession(): Promise<any> {
    return Auth.fetchAuthSession();
  }

  confirmSignIn(challengeResponse: string): Promise<any> {
    return Auth.confirmSignIn({ challengeResponse });
  }

  autoSignIn(): Promise<any> {
    return Auth.autoSignIn();
  }

  resendSMSCode(username: string): Promise<any> {
    return Auth.resendSignUpCode({ username });
  }

  resetPassword(
    username: string,
    code: string,
    password: string,
  ): Promise<any> {
    return Auth.confirmResetPassword({
      username: username,
      newPassword: password,
      confirmationCode: code,
    });
  }

  confirmResetPassword(
    username: string,
    confirmationCode: string,
    newPassword: string,
  ): Promise<any> {
    return Auth.confirmResetPassword({
      username,
      confirmationCode,
      newPassword,
    });
  }

  updatePassword(oldPassword: string, newPassword: string): Promise<any> {
    return Auth.updatePassword({ oldPassword, newPassword });
  }

  getUrlFile(s3Key: string): Promise<any> {
    return Storage.getUrl({ path: s3Key });
  }

  uploadFile(path: string, data: File) {
    return Storage.uploadData({ path, data });
  }

  updateUserAttributes(user, profile): Promise<any> {
    return Auth.updateUserAttributes({
      userAttributes: profile,
    });
  }

  verifyCurrentUserAttribute(attr) {
    return Auth.sendUserAttributeVerificationCode({
      userAttributeKey: attr,
    });
  }

  verifyCurrentUserAttributeSubmit(attr, code): Promise<any> {
    return Auth.confirmUserAttribute({
      userAttributeKey: attr,
      confirmationCode: code,
    });
  }

  getInfoCognitoUser(): Promise<any> {
    return Auth.getCurrentUser();
  }

  updateUserSettings(settings): Observable<any> {
    return this.http.patch(`/profiles/settings`, settings);
  }

  getUserInfo(): Observable<any> {
    return this.http.get(`/profiles`);
  }

  enableSMS(user) {
    return Auth.updateMFAPreference({ sms: 'ENABLED' });
  }

  updateUserInfo(profile): Observable<any> {
    return this.http.patch(`/profiles`, profile);
  }

  getUserActivities(params): Observable<any> {
    return this.http.get(`/profiles/activities`, params);
  }

  sendCode(username: string): Promise<any> {
    return Auth.resetPassword({ username });
  }

    forgotPassword(payload: { username: string }) {
      return this.http.post('/auth/forgot-password', payload);
    }

  callLogin() {
    const headerDict = {
      login: 'login',
    };
    return this.http.get(`/profiles`, {}, headerDict);
  }

  completeNewPassword(user, password, requireAttributes?) {
    return Auth.confirmSignIn({ challengeResponse: password });
  }

  setPreferredMFA(user) {
    return Auth.updateMFAPreference({ sms: 'PREFERRED' });
  }

  changePassword(oldPassword, newPassword): Promise<any> {
    return Auth.updatePassword({
      oldPassword: oldPassword,
      newPassword: newPassword,
    });
  }

  disableMFA(user) {
    return Auth.updateMFAPreference({ sms: 'DISABLED' });
  }

  resendEmail(body): Observable<any> {
    return this.http.post('/admin/users/resend-initial-password', body);
  }

  async uploadFileEncryption(
    folder: string,
    file: File,
    progressCallback?: any,
  ): Promise<{ key: string }> {
    try {
      const { credentials } = await this.fetchAuthSession();
      const { apiVersion, bucket, region } = environment.AWS_CONFIG.Storage.S3;
      const digit = LodashUtil.randomDigit();
      const identifier = this.profileService.companyIdentifier();
      const Key = `documents/${folder}/${identifier}/${digit}_${file.name}`;
      const params = {
        Bucket: bucket,
        Body: file,
        Key,
        ContentType: file.type,
        ServerSideEncryption: 'AES256',
      };

      AWS.config.update({
        region: region,
        credentials,
      });
      const s3 = new AWS.S3({
        apiVersion: apiVersion,
      });

      // If progressCallback is a function
      if (typeof progressCallback == 'function') {
        return new Promise((resolve, reject) => {
          s3.putObject(params, (err, data) => {
            if (err) reject(err);
            else resolve({ key: Key });
          }).on('httpUploadProgress', progressCallback);
        });
      }

      return new Promise((resolve, reject) => {
        s3.putObject(params, (err, data) => {
          if (err) reject(err);
          else resolve({ key: Key });
        });
      });
    } catch (error) {
      throw error;
    }
  }

  loginWithMicrosoft(): void {
    const oauth = environment.AWS_CONFIG.Auth.Cognito.loginWith.oauth;

    const clientId = oauth.msClientID;
    const redirectUri = oauth.msRedirectUrl;

    const scope = 'openid profile email';
    const responseType = 'id_token';
    const responseMode = 'fragment';
    const nonce = Math.random().toString(36).substring(2);

    const url =
      `https://login.microsoftonline.com/common/oauth2/v2.0/authorize` +
      `?client_id=${clientId}` +
      `&response_type=${responseType}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&response_mode=${responseMode}` +
      `&scope=${encodeURIComponent(scope)}` +
      `&nonce=${nonce}`;

    window.location.href = url;
  }
}
